import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { X } from "lucide-react";

const HISTORY_KEY = "__chroMobileNavigation";

function historyState(): Record<string, unknown> {
  const state = window.history.state;
  return state && typeof state === "object" ? state : {};
}

function hasNavigationHistoryEntry(): boolean {
  return historyState()[HISTORY_KEY] === true;
}

function currentLocation(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function removeNavigationHistoryMarker(): void {
  const { [HISTORY_KEY]: _marker, ...state } = historyState();
  window.history.replaceState(state, "");
}

const safeAreaStyle: CSSProperties = {
  paddingTop: "max(1rem, env(safe-area-inset-top))",
  paddingRight: "max(1rem, env(safe-area-inset-right))",
  paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
  paddingLeft: "max(1rem, env(safe-area-inset-left))",
};

interface SheetProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  description: string;
  children: ReactNode;
  initialFocusRef?: RefObject<HTMLElement>;
  restoreFocusRef?: RefObject<HTMLElement>;
}

/** The single mobile navigation modal used by the responsive application shell. */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  initialFocusRef,
  restoreFocusRef,
}: SheetProps) {
  const reactId = useId().replaceAll(":", "");
  const titleId = `${reactId}-title`;
  const descriptionId = `${reactId}-description`;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const onOpenChangeRef = useRef(onOpenChange);
  const openedAtRef = useRef("");
  const closingWithBackRef = useRef(false);
  const historyCleanupTimerRef = useRef<number>();

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  const requestClose = useCallback(() => {
    if (hasNavigationHistoryEntry()) {
      closingWithBackRef.current = true;
      window.history.back();
    } else {
      onOpenChangeRef.current(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    const returnFocus =
      restoreFocusRef?.current ??
      (document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null);
    const body = document.body;
    const root = document.documentElement;
    const scrollY = window.scrollY;
    const previousStyles = {
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      rootOverflow: root.style.overflow,
    };

    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    root.style.overflow = "hidden";

    if (!dialog.open) dialog.showModal();
    const focusFrame = window.requestAnimationFrame(() => {
      const initialFocus = initialFocusRef?.current;
      if (initialFocus && dialog.contains(initialFocus)) {
        initialFocus.focus();
        return;
      }
      dialog.querySelector<HTMLElement>("button, a[href], [tabindex='0']")?.focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
      if (dialog.open) dialog.close();
      body.style.overflow = previousStyles.bodyOverflow;
      body.style.position = previousStyles.bodyPosition;
      body.style.top = previousStyles.bodyTop;
      body.style.width = previousStyles.bodyWidth;
      root.style.overflow = previousStyles.rootOverflow;
      window.scrollTo(0, scrollY);
      if (returnFocus?.isConnected) returnFocus.focus();
    };
  }, [initialFocusRef, open, restoreFocusRef]);

  useEffect(() => {
    if (!open) return;

    if (historyCleanupTimerRef.current !== undefined) {
      window.clearTimeout(historyCleanupTimerRef.current);
      historyCleanupTimerRef.current = undefined;
    }
    closingWithBackRef.current = false;
    if (!hasNavigationHistoryEntry()) {
      window.history.pushState(
        { ...historyState(), [HISTORY_KEY]: true },
        ""
      );
    }
    openedAtRef.current = currentLocation();

    const onPopState = () => {
      if (!hasNavigationHistoryEntry()) {
        closingWithBackRef.current = false;
        onOpenChangeRef.current(false);
      }
    };
    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
      if (closingWithBackRef.current || !hasNavigationHistoryEntry()) return;

      // Route links close the drawer and replace the URL in the same event. Wait
      // for that navigation: remove the marker there, or traverse back when the
      // drawer alone closed on the same route.
      historyCleanupTimerRef.current = window.setTimeout(() => {
        historyCleanupTimerRef.current = undefined;
        if (!hasNavigationHistoryEntry()) return;
        if (currentLocation() === openedAtRef.current) window.history.back();
        else removeNavigationHistoryMarker();
      }, 0);
    };
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onCancel={(event) => {
        event.preventDefault();
        requestClose();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Tab") return;
        const focusable = Array.from(
          event.currentTarget.querySelectorAll<HTMLElement>(
            "a[href], button:not([disabled]), [tabindex='0']"
          )
        ).filter((element) => element.getClientRects().length > 0);
        const activeIndex = focusable.indexOf(document.activeElement as HTMLElement);
        if (focusable.length === 0) return;
        const direction = event.shiftKey ? -1 : 1;
        const start = activeIndex < 0 ? (event.shiftKey ? 0 : -1) : activeIndex;
        const next = (start + direction + focusable.length) % focusable.length;
        event.preventDefault();
        focusable[next]?.focus();
      }}
      onPointerDown={(event) => {
        const bounds = event.currentTarget.getBoundingClientRect();
        const outside =
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom;
        if (outside) requestClose();
      }}
      className="fixed inset-y-0 left-0 m-0 h-screen max-h-none w-[calc(100%-2.75rem)] max-w-sm overflow-hidden border-0 bg-bg p-0 text-fg shadow-2xl outline-none backdrop:bg-black/45 supports-[height:100dvh]:h-[100dvh]"
    >
      <div className="flex h-full min-w-0 flex-col overflow-hidden" style={safeAreaStyle}>
        <header className="flex shrink-0 items-start gap-3 border-b border-border pb-3">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-lg font-semibold leading-6 text-fg">
              {title}
            </h2>
            <p id={descriptionId} className="mt-1 text-sm leading-5 text-muted">
              {description}
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md border border-border bg-bg text-muted outline-none transition-colors hover:text-fg focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </header>
        <div
          role="region"
          aria-label={`${title} content`}
          tabIndex={0}
          className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain py-4 text-sm"
        >
          {children}
        </div>
      </div>
    </dialog>
  );
}
